import { FC, useRef, useEffect, useState } from 'react';
import { TextField } from '../components/TextField';
import { PhoneField } from '../contact-methods/PhoneField';
import { isPlatform } from '../utils/isPlatform';
import { createUseStyles } from 'react-jss';

export interface IProfileCollectionData {
    firstName: string;
    lastName: string;
    location: string;
    phone: string;
}

// How the location field presents itself. Asking a first-time caregiver for only a zip keeps the
// initial ask small, but the value is stored in a free-text location field that holds anything the
// user chooses to share (and that other surfaces let them edit as a plain address). Demanding a zip
// from someone who already stored "Burlington, VT" blocks a save they never asked to make, so the
// stricter presentation is reserved for values that can actually satisfy it.
export type IProfileLocationMode = 'zip' | 'address';

const FIVE_DIGIT_ZIP = /^\d{5}$/;

// matches the server's cap, so an over-long address is stopped here rather than at a failed save
const ADDRESS_MAX_LENGTH = 500;

const isZip = (value: string) => FIVE_DIGIT_ZIP.test(value.trim());

export const resolveLocationMode = (storedLocation?: string): IProfileLocationMode => {
    if (!(storedLocation || '').trim() || isZip(storedLocation || '')) {
        return 'zip';
    }
    return 'address';
};

export const isLocationValid = (value: string, mode: IProfileLocationMode) => {
    if (mode === 'zip') {
        return isZip(value);
    }
    return Boolean(value.trim());
};

const LocationField: FC<{ mode: IProfileLocationMode, value: string, onChange: (value: string) => void }> = ({ mode, value, onChange }) => {
    if (mode === 'address') {
        return (
            <TextField
                name="address"
                label="Address"
                value={value}
                autocapitalize="words"
                maxLength={ADDRESS_MAX_LENGTH}
                required
                onInput={onChange}
            />
        );
    }

    return (
        <TextField
            name="zip-code"
            label="Zip Code"
            value={value}
            placeholder="12345"
            type="text"
            inputmode="numeric"
            maxLength={5}
            required
            onInput={location => onChange(location.replace(/\D/g, '').slice(0, 5))}
        />
    );
};

type IProfileCollectionFormProps = {
    data: IProfileCollectionData;
    onChange: (data: IProfileCollectionData) => void;
    onValidityChange?: (isValid: boolean) => void;
    autoFocus?: boolean;

    // resolve once when the form is first shown and hold it: re-deriving per render would swap the
    // label and snap the 5-character limit on mid-edit as soon as the user cleared the field
    locationMode: IProfileLocationMode;
};

const useStyles = createUseStyles({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        width: '100%',
        maxWidth: 400
    }
});

export const ProfileCollectionForm: FC<IProfileCollectionFormProps> = ({ data, onChange, onValidityChange, autoFocus, locationMode }) => {
    const classes = useStyles();
    const [isPhoneValid, setIsPhoneValid] = useState(false);

    const firstNameRef = useRef<HTMLIonInputElement>(null);

    useEffect(() => {
        if (!autoFocus) {
            return;
        }
        if (isPlatform('desktop') || isPlatform('capacitor')) {
            const timer = setTimeout(() => {
                firstNameRef.current?.setFocus();
            }, 350);
            return () => clearTimeout(timer);
        }
    }, [autoFocus]);

    const locationValid = isLocationValid(data.location, locationMode);

    useEffect(() => {
        const isValid = Boolean(data.firstName.trim()) && locationValid && isPhoneValid;
        onValidityChange?.(isValid);
    }, [data.firstName, locationValid, isPhoneValid, onValidityChange]);

    return (
        <div className={classes.wrapper}>
            <TextField
                inputRef={firstNameRef}
                name="first-name"
                label="First Name"
                value={data.firstName}
                autocapitalize="words"
                required
                onInput={firstName => onChange({ ...data, firstName })}
            />
            <TextField
                name="last-name"
                label="Last Name"
                value={data.lastName}
                autocapitalize="words"
                onInput={lastName => onChange({ ...data, lastName })}
            />
            <LocationField
                mode={locationMode}
                value={data.location}
                onChange={location => onChange({ ...data, location })}
            />
            <PhoneField
                required
                isValid={setIsPhoneValid}
                method={{ type: 'mobile-phone', value: data.phone }}
                methodChanged={method => onChange({ ...data, phone: method.value })}
            />
        </div>
    );
};
